import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const AUDIT_FILTERS = [
  { value: "", label: "All events" },
  { value: "password_reset_succeeded", label: "Password reset succeeded" },
  { value: "forgot_password_requested", label: "Forgot-password requested" },
  { value: "forgot_password_unknown_email", label: "Forgot-password (unknown email)" },
  { value: "password_reset_replay_attempt", label: "Reset replay attempt" },
  { value: "password_reset_invalid_token", label: "Invalid reset token" },
  { value: "password_reset_expired", label: "Expired reset token" },
  { value: "login_locked", label: "Account locked" },
];

const EVENT_TONE = {
  password_reset_succeeded: "text-[hsl(var(--secondary))]",
  forgot_password_requested: "text-[hsl(var(--muted-foreground))]",
  forgot_password_unknown_email: "text-[hsl(var(--muted-foreground))]",
  password_reset_replay_attempt: "text-[hsl(var(--destructive))]",
  password_reset_invalid_token: "text-[hsl(var(--destructive))]",
  password_reset_expired: "text-[hsl(var(--destructive))]",
  login_locked: "text-[hsl(var(--destructive))]",
};

export default function AuditPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  async function load(ev) {
    try {
      const url = ev ? `/audit-log?event=${ev}&limit=300` : `/audit-log?limit=300`;
      const { data } = await api.get(url);
      setItems(data);
    } catch(e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(filter); })(); }, [filter]);

  return (
    <div data-testid="admin-audit-panel">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Security & audit · {items.length}</h3>
        <Select value={filter || "_all"} onValueChange={(v)=>setFilter(v === "_all" ? "" : v)}>
          <SelectTrigger data-testid="admin-audit-filter" className="w-72"><SelectValue /></SelectTrigger>
          <SelectContent>
            {AUDIT_FILTERS.map(f => (
              <SelectItem key={f.value || "_all"} value={f.value || "_all"} data-testid={`audit-filter-${f.value || "all"}`}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto bg-white border border-[hsl(var(--border))] rounded-2xl">
        <table className="w-full text-sm" data-testid="admin-audit-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <th className="p-4 font-normal">When</th><th className="p-4 font-normal">Event</th><th className="p-4 font-normal">Email</th><th className="p-4 font-normal">IP</th><th className="p-4 font-normal">Meta</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`admin-audit-row-${a.id}`} className="border-b border-[hsl(var(--border))]/60 last:border-0">
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{format(parseISO(a.created_at), "MMM dd HH:mm:ss")}</td>
                <td className={`p-4 font-medium ${EVENT_TONE[a.event] || ""}`}>{a.event}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{a.email || "—"}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))] font-mono text-xs">{a.ip || "—"}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))] text-xs">
                  {a.meta && Object.keys(a.meta).length > 0 ? JSON.stringify(a.meta) : "—"}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[hsl(var(--muted-foreground))]">No events yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
